import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCheck, Clipboard, Calendar, Loader2, BellOff } from 'lucide-react'
import { notificationService, type AppNotification } from '@/services/firebase/notifications'
import { useAuthStore } from '@/store/authStore'

const TYPE_CFG = {
  task_assigned:  { icon: Clipboard,  bg: 'bg-blue-100',   text: 'text-blue-600',  label: 'Task Assigned'  },
  task_status:    { icon: Bell,        bg: 'bg-violet-100', text: 'text-violet-600',label: 'Status Update'  },
  admin_message:  { icon: Bell,        bg: 'bg-amber-100',  text: 'text-amber-600', label: 'Admin Message'  },
}

const NotificationsPage = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loading, setLoading]             = useState(true)
  const [marking, setMarking]             = useState(false)

  useEffect(() => {
    if (!user) return
    const unsub = notificationService.subscribeToNotifications(user.uid, items => {
      setNotifications(items)
      setLoading(false)
    })
    return () => unsub()
  }, [user])

  const handleMarkAll = async () => {
    if (!user) return
    setMarking(true)
    await notificationService.markAllRead(user.uid)
    setMarking(false)
  }

  const handleMarkOne = (id: string) => notificationService.markRead(id)

  const unread = notifications.filter(n => !n.read).length

  return (
    <div className="space-y-5 pb-10">

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 50%,#0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(249,115,22,0.3) 0%,transparent 70%)', filter: 'blur(36px)' }} />
        </div>
        <div className="relative z-10 p-5 lg:p-7 flex items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-3">
              <Bell size={11} className="text-orange-300" />
              <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">Notifications</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-black">Notifications</h1>
            <p className="text-blue-300/70 text-sm mt-1">
              {unread > 0
                ? <span className="text-orange-300 font-black">{unread} unread</span>
                : 'All caught up'}
              {notifications.length > 0 && ` · ${notifications.length} total`}
            </p>
          </div>
          {unread > 0 && (
            <button onClick={handleMarkAll} disabled={marking}
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all hover:scale-[1.03] disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)', boxShadow: '0 4px 16px rgba(249,115,22,0.3)' }}>
              {marking ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 size={22} className="text-orange-400 animate-spin" />
          <p className="text-sm text-gray-400">Loading notifications…</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-16 h-16 rounded-3xl flex items-center justify-center bg-gray-50">
            <BellOff size={24} className="text-gray-300" />
          </div>
          <p className="text-sm font-bold text-gray-500">No notifications yet</p>
          <p className="text-xs text-gray-400">You'll be notified when tasks are assigned to you</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map(n => {
            const cfg = TYPE_CFG[n.type] ?? TYPE_CFG.task_assigned
            const Icon = cfg.icon
            return (
              <div key={n.id}
                onClick={() => {
                  if (!n.read) handleMarkOne(n.id)
                  if (n.taskId) navigate(`/tasks/${n.taskId}`)
                }}
                className={`relative bg-white rounded-2xl p-4 transition-all cursor-pointer hover:shadow-md ${
                  n.read ? 'opacity-70' : ''
                }`}
                style={{
                  boxShadow: n.read
                    ? '0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)'
                    : '0 2px 10px rgba(249,115,22,0.1), 0 0 0 1.5px rgba(249,115,22,0.2)',
                }}>
                {!n.read && (
                  <span className="absolute top-3.5 right-3.5 w-2 h-2 rounded-full bg-orange-500" />
                )}
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                    <Icon size={15} className={cfg.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-black ${n.read ? 'text-gray-500' : 'text-gray-900'}`}>{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed whitespace-pre-line">{n.body}</p>
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-gray-400">
                      <Calendar size={9} />
                      <span>{n.createdAt.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      {n.taskNumber && (
                        <span className="ml-1 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{n.taskNumber}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default NotificationsPage
